import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { EstimateAnalysisResult } from "@/types/estimateAnalysis";

export default function AnalyzeEstimatePage() {
  const [estimateText, setEstimateText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EstimateAnalysisResult | null>(null);
  const [error, setError] = useState("");

  const handleAnalyze = async () => {
    if (!estimateText.trim()) {
      setError("Wklej treść kosztorysu.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("analyze-estimate", {
        body: { estimateText },
      });

      if (error) {
        throw new Error(error.message || "Nie udało się wykonać analizy.");
      }

      setResult(data as EstimateAnalysisResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wystąpił nieznany błąd.");
    } finally {
      setLoading(false);
    }
  };

  const handleInsertExample = () => {
    setEstimateText(`Malowanie ścian – 180 m2 – 65 zł/m2
Układanie paneli – 55 m2 – 90 zł/m2
Montaż listew przypodłogowych – 48 mb – 35 zł/mb
Gruntowanie ścian – 180 m2 – 12 zł/m2
Szpachlowanie ubytków – 40 m2 – 55 zł/m2`);
    setError("");
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="rounded-3xl border p-6 md:p-8 bg-background">
        <div className="max-w-3xl">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            Analiza kosztorysu
          </h1>
          <p className="text-muted-foreground mt-3 text-base md:text-lg">
            Wklej treść istniejącego kosztorysu i sprawdź, które pozycje mogą być
            zawyżone, gdzie mieszczą się ceny rynkowe oraz jakie kwestie warto
            negocjować przed podjęciem decyzji.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 rounded-2xl border p-5 md:p-6 space-y-4">
          <div>
            <h2 className="text-xl font-semibold">Wklej treść kosztorysu</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Na tym etapie MVP analiza działa na podstawie wklejonego tekstu.
              Upload pliku dodamy w kolejnym kroku.
            </p>
          </div>

          <textarea
            value={estimateText}
            onChange={(e) => setEstimateText(e.target.value)}
            placeholder="Np. Malowanie ścian – 180 m2 – 65 zł/m2"
            className="w-full min-h-[320px] rounded-2xl border p-4 text-sm md:text-base"
          />

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleAnalyze}
              disabled={loading}
              className="rounded-xl px-5 py-3 border font-medium"
            >
              {loading ? "Analizowanie..." : "Analizuj kosztorys"}
            </button>

            <button
              type="button"
              onClick={handleInsertExample}
              disabled={loading}
              className="rounded-xl px-5 py-3 border font-medium"
            >
              Wstaw przykład
            </button>
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}
        </div>

        <div className="rounded-2xl border p-5 md:p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold">Jak przygotować kosztorys do analizy</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Im czytelniejszy format, tym lepsza analiza.
            </p>
          </div>

          <div className="rounded-xl border p-4 bg-muted/20">
            <p className="text-sm font-medium mb-2">Przykładowy układ:</p>
            <pre className="text-xs md:text-sm whitespace-pre-wrap text-muted-foreground">
{`Malowanie ścian – 180 m2 – 65 zł/m2
Układanie paneli – 55 m2 – 90 zł/m2
Montaż listew – 48 mb – 35 zł/mb`}
            </pre>
          </div>

          <div className="space-y-2 text-sm text-muted-foreground">
            <p>• Wklej pozycje możliwie linia po linii.</p>
            <p>• Dodaj jednostki, jeśli są dostępne.</p>
            <p>• Dodaj cenę za jednostkę lub wartość pozycji.</p>
            <p>• W kolejnej wersji dodamy możliwość wgrania pliku.</p>
          </div>
        </div>
      </div>

      {result && (
        <div className="space-y-6">
          <div className="rounded-2xl border p-5 md:p-6">
            <h2 className="text-xl font-semibold">Podsumowanie</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Poziom cen</div>
                <div className="text-lg font-semibold mt-1">
                  {result.summary.price_level}
                </div>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Potencjalna nadpłata</div>
                <div className="text-lg font-semibold mt-1">
                  {result.summary.estimated_overpay_min}–{result.summary.estimated_overpay_max} zł
                </div>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Pozycje do uwagi</div>
                <div className="text-lg font-semibold mt-1">
                  {result.summary.flagged_items_count}
                </div>
              </div>

              <div className="rounded-xl border p-4">
                <div className="text-sm text-muted-foreground">Wszystkie pozycje</div>
                <div className="text-lg font-semibold mt-1">
                  {result.summary.total_items}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border p-5 md:p-6">
            <h2 className="text-xl font-semibold">Pozycje do negocjacji</h2>
            <div className="mt-4 space-y-4">
              {result.flagged_items.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  Nie wykryto pozycji wyraźnie odbiegających od rynku.
                </div>
              ) : (
                result.flagged_items.map((item, index) => (
                  <div key={index} className="rounded-xl border p-4">
                    <div className="font-semibold">{item.name}</div>
                    <div className="text-sm mt-2">Cena w kosztorysie: {item.price_in_quote}</div>
                    <div className="text-sm">Zakres rynkowy: {item.market_range}</div>
                    <div className="text-sm">Ocena: {item.evaluation}</div>
                    <div className="text-sm mt-2 text-muted-foreground">{item.comment}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="rounded-2xl border p-5 md:p-6">
            <h2 className="text-xl font-semibold">Pozycje rynkowe</h2>
            <div className="mt-4 space-y-4">
              {result.ok_items.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  Brak pozycji zakwalifikowanych jako wyraźnie rynkowe.
                </div>
              ) : (
                result.ok_items.map((item, index) => (
                  <div key={index} className="rounded-xl border p-4">
                    <div className="font-semibold">{item.name}</div>
                    <div className="text-sm mt-2">Ocena: {item.evaluation}</div>
                    <div className="text-sm mt-2 text-muted-foreground">{item.comment}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="rounded-2xl border p-5 md:p-6">
              <h2 className="text-xl font-semibold">Ryzyka i braki</h2>
              <ul className="mt-4 space-y-2 list-disc pl-5">
                {result.risks.length === 0 ? (
                  <li className="text-muted-foreground">Brak dodatkowych ryzyk wskazanych przez analizę.</li>
                ) : (
                  result.risks.map((risk, index) => (
                    <li key={index}>{risk}</li>
                  ))
                )}
              </ul>
            </div>

            <div className="rounded-2xl border p-5 md:p-6">
              <h2 className="text-xl font-semibold">Sugestie negocjacyjne</h2>
              <ul className="mt-4 space-y-2 list-disc pl-5">
                {result.negotiation_tips.length === 0 ? (
                  <li className="text-muted-foreground">Brak dodatkowych sugestii negocjacyjnych.</li>
                ) : (
                  result.negotiation_tips.map((tip, index) => (
                    <li key={index}>{tip}</li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}